import {
    Box,
    FormControl,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select as MuiSelect,
    SelectChangeEvent,
    SelectProps as MuiSelectProps,
    SxProps,
} from '@mui/material'
import { isEmpty } from 'lodash'
import { ComponentProps, ElementType, ReactNode } from 'react'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { CaretDownIcon, XIcon } from '@phosphor-icons/react'
import { Icon } from 'react-feather'

export type SelectOption<TValue = string | number> = { value: TValue; label: ReactNode; disabled?: boolean }

type SelectGroup<TValue> = { label: string; options: SelectOption<TValue>[] }

export type GroupedOptions<TValue = string | number> = Record<string, SelectGroup<TValue>>

type SelectProps<TValue = string | number | string[], KValue = string | number | undefined> = {
    upperOptions?: SelectOption<KValue>[]
    options?: SelectOption<KValue>[]
    variant?: MuiSelectProps<TValue>['variant']
    groupedOptions?: GroupedOptions<KValue>
    className?: string
    inputLabel?: string
    selectProps?: MuiSelectProps<TValue>
    onChange: (event: SelectChangeEvent<TValue>) => void
    value: TValue
    disabled?: boolean
    multiple?: boolean
    noOptionsMessage?: string
    size?: 'small' | 'medium'
    error?: boolean
    fullWidth?: boolean
    sortedBy?: 'alphabetical'
    sortGroupOptions?: boolean
    shrinkLabel?: boolean
    renderOption?: (value: SelectOption<KValue>) => ReactNode
    formControlSx?: SxProps
    IconComponent?: ElementType
    clear?: () => void
    required?: boolean
}

const sortIfString = <TValue,>(a: SelectOption<TValue>, b: SelectOption<TValue>) => {
    if (typeof a.label === 'string' && typeof b.label === 'string') {
        return a.label.localeCompare(b.label)
    }
    return 0
}

export default function Select<TValue = string | number | string[], KValue = string | number | undefined>({
    upperOptions,
    options,
    variant,
    groupedOptions,
    className,
    selectProps,
    value,
    onChange,
    inputLabel = '',
    disabled = false,
    multiple = false,
    noOptionsMessage = 'Keine Optionen verfügbar',
    size = 'medium',
    sortedBy,
    sortGroupOptions = true,
    shrinkLabel,
    error,
    fullWidth,
    renderOption,
    formControlSx,
    IconComponent,
    clear,
    required = false,
}: SelectProps<TValue, KValue>) {
    const labelId = `${inputLabel}-label`
    const optionGroups = groupedOptions ? Object.values(groupedOptions) : undefined
    const sortedOptionGroups = sortedBy === 'alphabetical' ? optionGroups?.sort((a, b) => a.label.localeCompare(b.label)) : optionGroups
    const sortedUpperOptions = sortedBy === 'alphabetical' ? upperOptions?.sort(sortIfString) : upperOptions
    const sortedOptions = sortedBy === 'alphabetical' ? options?.sort(sortIfString) : options
    return (
        <FormControl
            className={className}
            size={size}
            fullWidth={fullWidth}
            required={required}
            sx={{ '& label': { maxWidth: `calc(100% - ${tokens.SizingSpacing600})` }, ...formControlSx }}
        >
            <InputLabel id={labelId} shrink={shrinkLabel}>
                {inputLabel}
            </InputLabel>
            <MuiSelect
                data-testid={`${inputLabel}-select`}
                error={error}
                label={inputLabel}
                labelId={labelId}
                {...selectProps}
                sx={{
                    ...selectProps?.sx,
                    ...(clear
                        ? {
                              '.clear-button': { display: 'none' },
                              '&:hover .clear-button': { display: 'flex' },
                              '&.Mui-focused .clear-button': { display: 'flex' },
                          }
                        : {}),
                    backgroundColor: disabled ? tokens.ColorSurfaceDisabled : undefined,
                    '& .MuiOutlinedInput-notchedOutline': disabled ? { borderColor: 'transparent!important' } : {},
                }}
                endAdornment={
                    <>
                        {selectProps?.endAdornment}
                        {!disabled && clear && value && !isEmpty(value) && (
                            <Box
                                className="clear-button"
                                position="absolute"
                                right={tokens.SizingSpacing400}
                                padding={tokens.SizingSpacing025}
                                sx={{
                                    bgcolor: tokens.ColorSurfaceDefault,
                                    cursor: 'pointer',
                                    alignItems: 'center',
                                    color: tokens.ColorIconSubtle,
                                    '&:hover': { color: tokens.ColorIconLinkHover },
                                }}
                                onClick={clear}
                            >
                                <XIcon size={tokens.SizingIconS} weight="bold" />
                            </Box>
                        )}
                    </>
                }
                onChange={onChange}
                value={value}
                disabled={disabled}
                multiple={multiple}
                variant={variant}
                IconComponent={IconComponent || CaretDown}
            >
                {sortedUpperOptions?.map((option) => (
                    // MenuItem is typed to not support complex values, even though it behaves as expected. any assertion overrides the restriction
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    <MenuItem key={`${option.value}`} value={option.value as any} disabled={!!option.disabled}>
                        {renderOption ? renderOption(option) : option.label}
                    </MenuItem>
                ))}
                {sortedOptionGroups?.map((optionGroup) => {
                    const sortedGroupOptions = sortGroupOptions ? optionGroup.options.sort(sortIfString) : optionGroup.options
                    return [
                        <ListSubheader
                            key={`reason-for-valuation-label-${optionGroup.label}`}
                            sx={{ ...tokens.Headline5, color: tokens.ColorTextSubtle, paddingY: tokens.SizingSpacing100 }}
                        >
                            {optionGroup.label}
                        </ListSubheader>,
                        sortedGroupOptions.map((option) => {
                            return (
                                // MenuItem is typed to not support complex values, even though it behaves as expected. any assertion overrides the restriction
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                <MenuItem key={`${option.value}`} value={option.value as any} sx={{ paddingLeft: '32px' }}>
                                    {renderOption ? renderOption(option) : option.label}
                                </MenuItem>
                            )
                        }),
                    ]
                })}
                {sortedOptions?.map((option) => (
                    // MenuItem is typed to not support complex values, even though it behaves as expected. any assertion overrides the restriction
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    <MenuItem key={`${option.value}`} value={option.value as any} disabled={!!option.disabled}>
                        {renderOption ? renderOption(option) : option.label}
                    </MenuItem>
                ))}
                {isEmpty(options) && isEmpty(groupedOptions) && <MenuItem disabled>{noOptionsMessage}</MenuItem>}
            </MuiSelect>
        </FormControl>
    )
}

export const CaretDown = (params: ComponentProps<Icon>) => (
    <CaretDownIcon
        {...params}
        size={tokens.SizingIconS}
        weight="bold"
        color={tokens.ColorIconSubtle}
        style={{
            marginLeft: tokens.SizingSpacing075,
            marginRight: tokens.SizingSpacing075,
        }}
    />
)
